'use client'

import { useMemo } from 'react'
import { useCart } from './useCart'
import { DEPOSIT_MIN_LEG } from '@/utils/order'

type Args = {
  vendor: any
  selectedDelivery: 'delivery' | 'pickup' | null
}

export function useOrderTotal({ vendor, selectedDelivery }: Args) {
  const { cart, amount, sale, giftCardDiscount, couponDiscount, orderDiscountAmount, hasFreeShipping, initialLoading, loyaltyPreview, loyaltyDiscountAmount, loyaltyVoucherDiscount } = useCart()
  const products = cart?.products || []

  return useMemo(() => {
    const subPrice = amount - sale
    /*
      Mirrors the backend's createOrder sequence: the coupon/discount system
      claims lines FIRST, then loyalty tier benefits (off /loyalty/checkout/preview,
      server-computed, display only), then gift card, then the voucher — which by
      default is consumed IN FULL even when it exceeds what's left, so the visible
      deduction is capped but the forfeited remainder is surfaced separately for
      the warning copy. Validating the coupon last instead zeroed it whenever
      loyalty had already eaten into subPrice (e.g. a free-type coupon on a
      loyalty-discounted product), showing a total the server would never charge.
    */
    const validCouponDiscount = couponDiscount > 0 && couponDiscount <= subPrice ? couponDiscount : 0
    const loyaltyDiscount = loyaltyDiscountAmount > 0 && loyaltyDiscountAmount <= (subPrice - validCouponDiscount) ? loyaltyDiscountAmount : 0
    const validGiftCardDiscount = giftCardDiscount > 0 && giftCardDiscount <= (subPrice - validCouponDiscount - loyaltyDiscount) ? giftCardDiscount : 0
    const remainingForVoucher = Math.max(0, subPrice - validCouponDiscount - loyaltyDiscount - validGiftCardDiscount - orderDiscountAmount)
    const validLoyaltyVoucherDiscount = loyaltyVoucherDiscount > 0 ? Math.min(loyaltyVoucherDiscount, remainingForVoucher) : 0
    const loyaltyVoucherForfeit = loyaltyVoucherDiscount > 0 ? Math.max(0, loyaltyVoucherDiscount - validLoyaltyVoucherDiscount) : 0

    let deliveryPrice = 0
    const allDigital = products.length > 0 && products.every((p: any) => p.isDigital === true)
    const loyaltyFreeShipping = !!loyaltyPreview?.freeShipping
    if (allDigital || selectedDelivery === 'pickup' || hasFreeShipping || loyaltyFreeShipping) {
      deliveryPrice = 0
    } else {
      const deliveryType = vendor?.deliveryType
      const vendorDeliveryPrice = vendor?.deliveryPrice
      const minOrderAmount = vendor?.minOrderAmount
      switch (deliveryType) {
        case 'constantDelivery':
          deliveryPrice = vendorDeliveryPrice || 0
          break
        case 'minOrderAmount':
          deliveryPrice = subPrice >= (minOrderAmount || 0) ? 0 : (vendorDeliveryPrice || 0)
          break
        default:
          deliveryPrice = 0
      }
      if (deliveryPrice > 0 && loyaltyPreview?.shippingDiscountPercent > 0) {
        deliveryPrice = Math.max(0, deliveryPrice - Math.round(deliveryPrice * loyaltyPreview.shippingDiscountPercent / 100))
      }
    }

    const totalPrice = subPrice - loyaltyDiscount - validGiftCardDiscount - validLoyaltyVoucherDiscount - validCouponDiscount - orderDiscountAmount + deliveryPrice
    const isFreeOrder = totalPrice <= 0 && !initialLoading && products.length > 0

    /*
      Split payment (урьдчилгаа) — a PREDICTION of what the vendor will charge up front.

      The client sends no deposit figure, exactly as it sends no total: createOrder
      recomputes both server-side from the same rule. This has to mirror computeDeposit
      in the backend's lib/deposit.util.js — if the two disagree the customer sees one
      number at checkout and a different one on the payment screen.

      Digital carts are excluded for the same reason the backend excludes them: a file is
      released on full payment, so a deposit would only delay access.
    */
    let depositAmount = 0
    const depositBase = totalPrice - deliveryPrice
    if (vendor?.canDeposit && !isFreeOrder && !allDigital && depositBase > 0 && totalPrice >= 2 * DEPOSIT_MIN_LEG) {
      const raw = vendor.depositType === 'amount'
        ? (vendor.depositFixedAmount || 0)
        : Math.round((depositBase * (vendor.depositPercent || 0)) / 100)
      const candidate = Math.min(raw, depositBase)
      // Both legs must independently clear the floor, or no rail will accept one of
      // them — in which case the customer simply pays in full.
      if (candidate >= DEPOSIT_MIN_LEG && candidate < totalPrice && (totalPrice - candidate) >= DEPOSIT_MIN_LEG) {
        depositAmount = candidate
      }
    }
    const hasDeposit = depositAmount > 0
    const balanceAmount = hasDeposit ? totalPrice - depositAmount : 0
    const payNowAmount = hasDeposit ? depositAmount : totalPrice

    return { subPrice, validGiftCardDiscount, validCouponDiscount, loyaltyDiscount, validLoyaltyVoucherDiscount, loyaltyVoucherForfeit, loyaltyFreeShipping, deliveryPrice, totalPrice, isFreeOrder, depositAmount, balanceAmount, hasDeposit, payNowAmount }
  }, [amount, sale, giftCardDiscount, couponDiscount, orderDiscountAmount, hasFreeShipping, initialLoading, products, vendor, selectedDelivery, loyaltyPreview, loyaltyDiscountAmount, loyaltyVoucherDiscount])
}
